/* eslint-disable react-hooks/exhaustive-deps */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useSnackbar } from "notistack";
import AppError from "@utils/AppError";
import { useIoCContext } from "@context/IoCContext/IoCContext";
import {
  Discount,
  GasStationProduct,
  IDiscountService,
  MappedDiscount,
  NewDiscount,
} from "@modules/discount/model/IDiscountService";
import { Types } from "@ioc/types";
import { useHomeUserContext } from "@context/HomeUserContext";

interface DiscountContextProps {
  loading: boolean;
  discounts: GasStationProduct | null;
  selectedCNPJ: string | null;
  mappedDiscounts: Discount[];
  setLoading: (value: boolean) => void;
  setDiscounts: (data: GasStationProduct | null) => void;
  handleSave: (discount: NewDiscount) => Promise<any>;
  getDiscounts: () => void;
  setSelectedCNPJ: (cnpj: string | null) => void;
  isLoadingSaveDiscount: boolean;
  setIsLoadingSaveDiscount: React.Dispatch<React.SetStateAction<boolean>>;
}

const DiscountContext = createContext<DiscountContextProps>(
  {} as DiscountContextProps
);

const DiscountProvider: React.FC = ({ children }) => {
  const [selectedCNPJ, setSelectedCNPJ] = useState<string | null>(null);
  const iocContext = useIoCContext();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [discounts, setDiscounts] = useState<GasStationProduct | null>(null);
  const [isLoadingSaveDiscount, setIsLoadingSaveDiscount] = useState(false);
  const { documentSelected } = useHomeUserContext();
  const discountService = iocContext.serviceContainer.get<IDiscountService>(
    Types.Discount.IDiscountService
  );

  const mappedDiscounts = useMemo(() => {
    let mapped: Discount[] = [];
    if (discounts) {
      const keys = Object.keys(discounts);
      keys.forEach((key) => {
        const productData = discounts?.[key][0].product;
        const newDiscounts = discounts?.[key];

        const newMappedDiscount: Discount = {
          discounts: newDiscounts.map((discount) => ({
            paymentMethod: discount.paymentMethod,
            discount: discount.value,
            discountId: discount.id,
          })) as MappedDiscount[],
          productId: productData.id ?? "",
          internalCode: productData.internal_code,
          description: productData.name,
          unit: productData.unit,
          value: productData.value ?? "",
        };
        mapped.push(newMappedDiscount);
      });
    }
    return mapped;
  }, [discounts]);

  const getDiscounts = useCallback(async () => {
    try {
      if (documentSelected.isSelected) {
        setLoading(true);
        const response = await discountService.listDiscounts(
          documentSelected.cnpj
        );
        setDiscounts(response);
      }
    } catch (error) {
      enqueueSnackbar(
        `Falha ao buscar desconto! ${(error as AppError).message} `,
        {
          variant: "error",
        }
      );
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, documentSelected]);

  useEffect(() => {
    getDiscounts();
  }, [getDiscounts]);

  const handleSave = useCallback(
    async (discount: NewDiscount) => {
      try {
        if (selectedCNPJ) {
          setIsLoadingSaveDiscount(true);
          const response = await discountService.createDiscount(
            selectedCNPJ,
            discount
          );
          enqueueSnackbar("Desconto editado com sucesso!", {
            variant: "success",
          });
          return response;
        }
      } catch (error) {
        enqueueSnackbar(
          `Falha ao buscar desconto! ${(error as AppError).message} `,
          {
            variant: "error",
          }
        );
      } finally {
        setIsLoadingSaveDiscount(false);
      }
    },
    [enqueueSnackbar, selectedCNPJ]
  );

  return (
    <DiscountContext.Provider
      value={{
        loading,
        discounts,
        selectedCNPJ,
        mappedDiscounts,
        setLoading,
        setDiscounts,
        handleSave,
        getDiscounts,
        setSelectedCNPJ,
        isLoadingSaveDiscount,
        setIsLoadingSaveDiscount,
      }}
    >
      {children}
    </DiscountContext.Provider>
  );
};

const useDiscountContext = () => {
  const context = useContext(DiscountContext);
  if (Object.values(context).length === 0) {
    throw new Error(
      "useDiscountContext deve ser utilizado dentro de um DiscountProvider"
    );
  }
  return context;
};

export { useDiscountContext, DiscountProvider };
